import React, { useState, useEffect } from 'react';
import { useVideoList, useVideoCacheManager } from '@/hooks/useVideoCache';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Grid, List, Search, Filter, Upload, RefreshCw } from 'lucide-react';
import UnifiedVideoCard from '@/components/UnifiedVideoCard';
import { Link } from 'react-router-dom';
import DocumentTitle from '@/components/DocumentTitle';

const YourVideos = () => {
  const { session } = useAuth();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchIndexedFilter, setSearchIndexedFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Use the new cached video list hook
  const { data: videos, isLoading, error, refetch } = useVideoList(page, pageSize);
  
  // Use cache manager for manual refresh
  const { refreshVideoList } = useVideoCacheManager();

  const filteredVideos = videos?.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || video.status === statusFilter;
    
    const matchesSearchIndexed = searchIndexedFilter === 'all' || video.search_indexed === searchIndexedFilter;
    
    return matchesSearch && matchesStatus && matchesSearchIndexed;
  }) || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Your Videos</h1>
          <Button asChild>
            <Link to="/upload">
              <Upload className="w-4 h-4 mr-2" />
              Upload Video
            </Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Error Loading Videos</h2>
        <p className="text-muted-foreground mb-4">
          {error instanceof Error ? error.message : 'An error occurred while loading your videos.'}
        </p>
        <Button onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DocumentTitle 
        title="Your Videos - VideoBrain.ai | Manage Your Video Library"
        description="Access and manage your video library on VideoBrain.ai. Upload, process, and search through your video content with advanced AI tools."
      />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Your Videos</h1>
          <Button asChild>
            <Link to="/upload">
              <Upload className="w-4 h-4 mr-2" />
              Upload Video
            </Link>
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="PROCESSING">Processing</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={searchIndexedFilter} onValueChange={setSearchIndexedFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Filter by search indexing" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Videos</SelectItem>
              <SelectItem value="true">Indexed for Search</SelectItem>
              <SelectItem value="false">Not Indexed</SelectItem>
              <SelectItem value="processing">Currently Indexing</SelectItem>
              <SelectItem value="failed">Indexing Failed</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={refreshVideoList}
              title="Refresh video list"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {filteredVideos.length === 0 ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold mb-4">No Videos Found</h2>
            <p className="text-muted-foreground mb-6">
              {searchQuery || statusFilter !== 'all' || searchIndexedFilter !== 'all'
                ? 'Try adjusting your filters or search query.'
                : 'Upload your first video to get started.'}
            </p>
            {!searchQuery && statusFilter === 'all' && searchIndexedFilter === 'all' && (
              <Button asChild>
                <Link to="/upload">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Your First Video
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : "space-y-4"
          }>
            {filteredVideos.map((video) => (
              <UnifiedVideoCard key={video.id} video={video} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default YourVideos; 